import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { UtensilsCrossed, ArrowLeft, Phone, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [step, setStep] = useState(1); // 1: info, 2: OTP, 3: done
  const [form, setForm] = useState({ name: '', email: '', password: '', restaurant_name: '', mobile: '', whatsapp_same: true });
  const [otp, setOtp] = useState('');
  const [otpDebug, setOtpDebug] = useState('');
  const [otpMode, setOtpMode] = useState('mock'); // 'mock' | 'sms'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [trialDays, setTrialDays] = useState(7);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/site-settings`)
      .then(r => setTrialDays(r.data?.trial_days || 7))
      .catch(() => {});
  }, []);

  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  // Step 1: Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.restaurant_name || !form.mobile) {
      setError('All fields are required');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/send-otp`, { mobile: form.mobile, purpose: 'register' });
      setOtpDebug(data.otp_debug || '');
      setOtpMode(data.mode || 'mock');
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to send OTP');
    }
    setLoading(false);
  };

  // Step 2: Verify OTP & Register
  const handleVerifyAndRegister = async () => {
    if (!otp || otp.length < 4) { setError('Enter the OTP code'); return; }
    setError('');
    setLoading(true);
    try {
      // Verify OTP
      await axios.post(`${API}/auth/verify-otp`, { mobile: form.mobile, otp });
      // Register
      const result = await register(form.email, form.password, form.name, form.restaurant_name, form.mobile, form.whatsapp_same ? form.mobile : '');
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Verification failed');
    }
    setLoading(false);
  };

  const resendOtp = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/send-otp`, { mobile: form.mobile, purpose: 'register' });
      setOtpDebug(data.otp_debug || '');
      setOtpMode(data.mode || 'mock');
      setError('');
    } catch {}
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in-up">
        <Link to="/" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-8 transition-colors">
          <ArrowLeft size={18} /><span className="text-sm font-medium">Back to Home</span>
        </Link>

        <Card className="border-stone-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 flex items-center justify-center mb-3">
              {step === 3 ? <CheckCircle className="text-green-600" size={32} /> : <img src="/olafel_logo.png" alt="Olafel" className="w-full h-full object-contain" />}
            </div>
            <CardTitle className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
              {step === 1 ? 'Create your account' : step === 2 ? 'Verify your mobile' : 'All set!'}
            </CardTitle>
            <CardDescription className="text-stone-500">
              {step === 1 ? `Start your ${trialDays}-day free trial` : step === 2 ? `Enter the OTP sent to ${form.mobile}` : 'Your account is ready'}
            </CardDescription>
            {/* Step indicators */}
            <div className="flex items-center justify-center gap-2 mt-3">
              {[1, 2].map(s => (
                <div key={s} className={`w-8 h-1.5 rounded-full transition-all ${step >= s ? 'bg-orange-600' : 'bg-stone-200'}`} />
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {error && <div data-testid="register-error" className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 mb-4">{error}</div>}

            {/* Step 1: Info */}
            {step === 1 && (
              <form onSubmit={handleSendOtp} className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-stone-700">Full Name</Label>
                  <Input data-testid="register-name-input" value={form.name} onChange={update('name')} placeholder="John Doe" required className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-stone-700">Restaurant Name</Label>
                  <Input data-testid="register-restaurant-input" value={form.restaurant_name} onChange={update('restaurant_name')} placeholder="My Restaurant" required className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-stone-700">Email</Label>
                  <Input data-testid="register-email-input" type="email" value={form.email} onChange={update('email')} placeholder="you@restaurant.com" required className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-stone-700">Password</Label>
                  <Input data-testid="register-password-input" type="password" value={form.password} onChange={update('password')} placeholder="Min 6 characters" required minLength={6} className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-stone-700 flex items-center gap-1.5"><Phone size={14} /> Contact Mobile Number *</Label>
                  <Input data-testid="register-mobile-input" type="tel" value={form.mobile} onChange={update('mobile')} placeholder="e.g. +960 7XXXXXX" required className="h-11" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={form.whatsapp_same} onChange={e => setForm(p => ({ ...p, whatsapp_same: e.target.checked }))} className="w-4 h-4 accent-orange-600" />
                  <span className="text-sm text-stone-600">Same number for WhatsApp</span>
                </div>
                <Button data-testid="register-submit-button" type="submit" disabled={loading} className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-semibold mt-2">
                  {loading ? <><Loader2 size={16} className="animate-spin mr-2" /> Sending OTP...</> : 'Continue & Verify Mobile'}
                </Button>
              </form>
            )}

            {/* Step 2: OTP Verify */}
            {step === 2 && (
              <div className="space-y-4">
                {otpMode === 'sms' ? (
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-center">
                    <Phone size={24} className="mx-auto text-green-600 mb-2" />
                    <p className="text-sm font-medium text-green-800">OTP sent via SMS</p>
                    <p className="text-xs text-green-600 mt-1">Check your phone for a 6-digit code at {form.mobile}</p>
                  </div>
                ) : otpDebug ? (
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                    <p className="text-xs text-blue-600 mb-1">Development Mode — OTP Code:</p>
                    <p className="text-2xl font-bold text-blue-800 tracking-widest" style={{ fontFamily: 'Outfit' }}>{otpDebug}</p>
                  </div>
                ) : null}
                <div className="space-y-1.5">
                  <Label className="text-stone-700">Enter OTP Code</Label>
                  <Input data-testid="register-otp-input" value={otp} onChange={e => setOtp(e.target.value)} placeholder="6-digit code" maxLength={6} className="h-12 text-center text-lg tracking-widest font-bold" />
                </div>
                <Button data-testid="register-verify-button" onClick={handleVerifyAndRegister} disabled={loading} className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-semibold">
                  {loading ? <><Loader2 size={16} className="animate-spin mr-2" /> Verifying...</> : 'Verify & Create Account'}
                </Button>
                <div className="flex items-center justify-between">
                  <button onClick={() => setStep(1)} className="text-sm text-stone-500 hover:text-stone-700">Back</button>
                  <button onClick={resendOtp} disabled={loading} className="text-sm text-orange-600 hover:text-orange-700 font-medium">Resend OTP</button>
                </div>
              </div>
            )}

            <p className="text-center text-sm text-stone-500 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-orange-600 hover:text-orange-700 font-medium">Sign in</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
